import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Trophy, Flag, Users, TrendingUp } from 'lucide-react';
import { NationalityDistribution, GenderDistribution } from '../../../services/userManagementService';

interface DemographicsPanelProps {
  nationalityData: NationalityDistribution[];
  genderData: GenderDistribution[];
  topSavers: Array<{
    name: string;
    savings: number;
    nationality: string;
  }>;
}

const DemographicsPanel: React.FC<DemographicsPanelProps> = ({
  nationalityData,
  genderData,
  topSavers
}) => {
  const NATIONALITY_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];
  const GENDER_COLORS = ['#6366F1', '#EC4899', '#10B981', '#F59E0B'];

  return (
    <div className="space-y-6">
      {/* Nationality Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Flag className="h-5 w-5 text-blue-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Nationality Distribution</h3>
        </div>
        
        <div className="h-64 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={nationalityData.slice(0, 6)} // Show top 6
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count"
                label={({ nationality, percentage }) => `${nationality}: ${percentage}%`}
              >
                {nationalityData.slice(0, 6).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={NATIONALITY_COLORS[index % NATIONALITY_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: any, name: any) => [`${value} users`, 'Count']}
                labelFormatter={(label: any) => `Nationality: ${label}`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        <div className="space-y-2">
          {nationalityData.slice(0, 5).map((item, index) => (
            <div key={item.nationality} className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: NATIONALITY_COLORS[index % NATIONALITY_COLORS.length] }}
                ></div>
                <span className="text-gray-700">{item.nationality}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{item.count}</span>
                <span className="text-gray-500">({item.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gender Distribution */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Users className="h-5 w-5 text-purple-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Gender Breakdown</h3>
        </div>
        
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={genderData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="gender" />
              <YAxis />
              <Tooltip 
                formatter={(value: any, name: any) => [`${value} users`, 'Count']}
                labelFormatter={(label: any) => `Gender: ${label}`}
              />
              <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 space-y-2">
          {genderData.map((item, index) => (
            <div key={item.gender} className="flex items-center justify-between text-sm">
              <div className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: GENDER_COLORS[index % GENDER_COLORS.length] }}
                ></div>
                <span className="text-gray-700">{item.gender}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">{item.count}</span>
                <span className="text-gray-500">({item.percentage}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Savers Leaderboard */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Trophy className="h-5 w-5 text-yellow-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Top Savers</h3>
        </div>
        
        <div className="space-y-3">
          {topSavers.map((saver, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white mr-3 ${
                  index === 0 ? 'bg-yellow-500' :
                  index === 1 ? 'bg-gray-400' :
                  index === 2 ? 'bg-orange-600' : 'bg-blue-500'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <div className="font-medium text-gray-900">{saver.name}</div>
                  <div className="text-sm text-gray-500">{saver.nationality}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-green-600">${saver.savings.toFixed(2)}</div>
                <div className="text-sm text-gray-500">saved</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <TrendingUp className="h-5 w-5 text-green-500 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Quick Insights</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Most Common Nationality</span>
            <span className="font-medium text-gray-900">
              {nationalityData[0]?.nationality || 'N/A'}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Gender Diversity</span>
            <span className="font-medium text-gray-900">
              {genderData.length} categories
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Countries Represented</span>
            <span className="font-medium text-gray-900">
              {nationalityData.length}
            </span>
          </div>
          
          <div className="flex justify-between items-center py-2">
            <span className="text-sm text-gray-600">Top Saver Amount</span>
            <span className="font-medium text-green-600">
              ${topSavers[0]?.savings?.toFixed(2) || '0.00'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemographicsPanel;